import { AGENT_URL } from '@/config/env'
import {
  CALL_DETAIL_TIMEOUT_MS,
  RECORDING_DOWNLOAD_TIMEOUT_MS,
  RECORDING_STAGE_TIMEOUT_MS,
} from '@/config/constants'
import { mutateJsonWithAuth } from '@/lib/api'
import type { CallDetail, TestCallFeedbackNote } from '@/features/calls/types'

export interface UpdateCallFeedbackResponse {
  data: {
    call_id: string
    call_feedback: string | null
  }
}

export type AppendTestCallFeedbackBody = Pick<
  TestCallFeedbackNote,
  'text' | 'sentiment' | 'phase' | 'at_seconds'
>

export type RecordingType = 'stereo' | 'mono'

interface ResolveCallRecordingUrlParams {
  callId: string
  providerCallId?: string | null
  recordingType: RecordingType
}

export async function updateCallFeedback(
  token: string,
  callId: string,
  callFeedback: string | null
): Promise<UpdateCallFeedbackResponse> {
  return mutateJsonWithAuth<UpdateCallFeedbackResponse>({
    token,
    url: `${AGENT_URL}/data/calls/${encodeURIComponent(callId)}`,
    method: 'PATCH',
    body: { call_feedback: callFeedback },
    errorMessage: `Failed to update call feedback for callId="${callId}"`,
  })
}

/** POST /api/data/calls/{callId}/test-call-feedback — returns every note on the call. */
export async function appendTestCallFeedback(
  token: string,
  callId: string,
  note: AppendTestCallFeedbackBody
): Promise<{ notes: TestCallFeedbackNote[] }> {
  return mutateJsonWithAuth({
    token,
    url: `${AGENT_URL}/data/calls/${encodeURIComponent(callId)}/test-call-feedback`,
    method: 'POST',
    body: note,
    errorMessage: 'Failed to save feedback',
  })
}

export async function resolveCallRecordingUrl(
  token: string,
  { callId, providerCallId, recordingType }: ResolveCallRecordingUrlParams
): Promise<string> {
  const search = new URLSearchParams({ recording_type: recordingType })
  if (providerCallId?.trim()) {
    search.set('provider_call_id', providerCallId.trim())
  }

  let response: Response
  try {
    response = await fetch(
      `${AGENT_URL}/data/calls/${encodeURIComponent(
        callId
      )}/recording-url?${search.toString()}`,
      {
        headers: { Authorization: token },
        signal: AbortSignal.timeout(RECORDING_STAGE_TIMEOUT_MS),
      }
    )
  } catch (e) {
    throw new Error(
      `Could not reach the backend for recording URL callId="${callId}"`,
      { cause: e }
    )
  }

  if (!response.ok) {
    throw new Error(
      `Failed to resolve recording URL for callId="${callId}" (HTTP ${response.status})`
    )
  }

  try {
    const json = (await response.json()) as { url?: unknown }
    if (typeof json.url !== 'string' || !json.url) {
      throw new Error('Response did not include a recording URL')
    }
    return json.url
  } catch (e) {
    throw new Error(
      `Could not parse recording URL response for callId="${callId}"`,
      {
        cause: e,
      }
    )
  }
}

export interface DownloadedCallRecording {
  blob: Blob
  /** Suggested filename, read from the response's `Content-Disposition`. */
  filename: string
}

/** `Content-Disposition: attachment; filename="..."` → `...`. */
function parseAttachmentFilename(header: string | null): string | null {
  if (!header) return null
  const match = /filename="([^"]*)"/.exec(header)
  return match?.[1]?.trim() || null
}

/**
 * GET /api/data/calls/{callId}/recording-download — fetches the call
 * recording as a blob through our own backend rather than the signed
 * Vapi/R2 URL directly.
 *
 * Two things playback's signed URL can't give the Download button: this
 * always resolves a *fresh* URL server-side (see
 * `get_call_recording_download_handler`), so a sheet left open past the
 * signed URL's short lifetime still downloads instead of hitting an expired
 * link; and the response comes back same-origin with `Content-Disposition:
 * attachment` set by our server, so `triggerDownload`'s `<a download>` is
 * honoured reliably — a plain signed URL is cross-origin and carries neither
 * that header nor CORS headers permissive enough for the browser to
 * `fetch()` it directly (see the CORS note on `useCallDetail`'s
 * stereo/mono URL handling), so clicking a link pointed straight at it could
 * just navigate the app to the audio instead of saving a file.
 */
export async function downloadCallRecording(
  token: string,
  { callId, providerCallId, recordingType }: ResolveCallRecordingUrlParams
): Promise<DownloadedCallRecording> {
  const search = new URLSearchParams({ recording_type: recordingType })
  if (providerCallId?.trim()) {
    search.set('provider_call_id', providerCallId.trim())
  }

  let response: Response
  try {
    response = await fetch(
      `${AGENT_URL}/data/calls/${encodeURIComponent(
        callId
      )}/recording-download?${search.toString()}`,
      {
        headers: { Authorization: token },
        signal: AbortSignal.timeout(RECORDING_DOWNLOAD_TIMEOUT_MS),
      }
    )
  } catch (e) {
    throw new Error(
      `Could not reach the backend for recording download callId="${callId}"`,
      { cause: e }
    )
  }

  if (!response.ok) {
    throw new Error(
      `Failed to download recording for callId="${callId}" (HTTP ${response.status})`
    )
  }

  const blob = await response.blob()
  const filename =
    parseAttachmentFilename(response.headers.get('content-disposition')) ??
    `call-${callId}-recording`
  return { blob, filename }
}

export async function fetchCallDetail(
  token: string,
  callId: string,
  providerCallId?: string | null
): Promise<CallDetail | null> {
  let response: Response
  const search = new URLSearchParams()
  if (providerCallId?.trim()) {
    search.set('provider_call_id', providerCallId.trim())
  }
  const query = search.toString()
  const url = `${AGENT_URL}/data/calls/${encodeURIComponent(callId)}${
    query ? `?${query}` : ''
  }`
  try {
    response = await fetch(url, {
      headers: { Authorization: token },
      // Bounded for the same reason as the resolver: an unbounded hang here
      // leaves the query pending forever, and the Retry surface this feeds is
      // driven by `error`, so it would be unreachable.
      signal: AbortSignal.timeout(CALL_DETAIL_TIMEOUT_MS),
    })
  } catch (e) {
    // Throw so React Query's error state activates — consumers (CallDetailPage,
    // CallLogsSheetFacts) need this to render Retry / failure UI. Returning
    // null silently would make those branches unreachable in production.
    throw new Error(
      `Could not reach the backend for call detail callId="${callId}"`,
      { cause: e }
    )
  }

  if (response.status === 404) return null
  if (!response.ok) {
    throw new Error(
      `Failed to fetch call detail for callId="${callId}" (HTTP ${response.status})`
    )
  }

  try {
    const json = await response.json()
    return (json.data as CallDetail) ?? null
  } catch (e) {
    throw new Error(`Could not parse response JSON for callId="${callId}"`, {
      cause: e,
    })
  }
}

export async function fetchVapiCallDetail(
  token: string,
  callId: string
): Promise<CallDetail | null> {
  let response: Response
  try {
    response = await fetch(
      `${AGENT_URL}/data/vapi/call/${encodeURIComponent(callId)}`,
      {
        headers: { Authorization: token },
        signal: AbortSignal.timeout(CALL_DETAIL_TIMEOUT_MS),
      }
    )
  } catch (e) {
    throw new Error(
      `Could not reach the backend for VAPI call detail callId="${callId}"`,
      { cause: e }
    )
  }

  if (response.status === 404) return null
  if (!response.ok) {
    throw new Error(
      `Failed to fetch VAPI call detail for callId="${callId}" (HTTP ${response.status})`
    )
  }

  try {
    const json = await response.json()
    return (json.data as CallDetail) ?? null
  } catch (e) {
    throw new Error(`Could not parse response JSON for callId="${callId}"`, {
      cause: e,
    })
  }
}
